/**
 * Button Component
 * Premium button with gradient variants, glow effects, and Lucide icons support
 */

import React from "react";
import { cn } from "../../utils";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | "primary"
    | "secondary"
    | "danger"
    | "success"
    | "warning"
    | "ghost"
    | "info"
    | "error"
    | "gradient"
    | "outline";
  size?: "sm" | "md" | "lg" | "xl";
  loading?: boolean;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  fullWidth?: boolean;
  glow?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = "primary",
  size = "md",
  loading = false,
  icon,
  iconRight,
  fullWidth = false,
  glow = false,
  className,
  disabled,
  ...props
}) => {
  const baseStyles = `
    inline-flex items-center justify-center gap-2
    rounded-xl font-semibold
    transition-all duration-300 ease-out
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-navy-900
    disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
    active:scale-[0.98]
  `;

  const variants = {
    primary: `
      bg-gradient-to-r from-primary-500 to-primary-600
      hover:from-primary-400 hover:to-primary-500
      text-white shadow-lg shadow-primary-500/25
      hover:shadow-xl hover:shadow-primary-500/30
      hover:-translate-y-0.5
      focus:ring-primary-500
    `,
    gradient: `
      bg-gradient-to-r from-primary-500 via-accent-500 to-accent-600
      hover:from-primary-400 hover:via-accent-400 hover:to-accent-500
      text-white shadow-lg
      hover:shadow-xl hover:shadow-accent-500/30
      hover:-translate-y-0.5
      focus:ring-accent-500
    `,
    secondary: `
      bg-slate-800/80 backdrop-blur-sm
      hover:bg-slate-700/80
      text-slate-100
      border border-slate-700/50 hover:border-slate-600/50
      focus:ring-slate-500
    `,
    outline: `
      bg-transparent
      border-2 border-primary-500/50 hover:border-primary-400
      text-primary-400 hover:text-primary-300
      hover:bg-primary-500/10
      focus:ring-primary-500
    `,
    danger: `
      bg-gradient-to-r from-red-500 to-red-600
      hover:from-red-400 hover:to-red-500
      text-white shadow-lg shadow-red-500/25
      hover:shadow-xl hover:shadow-red-500/30
      hover:-translate-y-0.5
      focus:ring-red-500
    `,
    error: `
      bg-gradient-to-r from-red-500 to-red-600
      hover:from-red-400 hover:to-red-500
      text-white shadow-lg shadow-red-500/25
      focus:ring-red-500
    `,
    success: `
      bg-gradient-to-r from-emerald-500 to-emerald-600
      hover:from-emerald-400 hover:to-emerald-500
      text-white shadow-lg shadow-emerald-500/25
      hover:shadow-xl hover:shadow-emerald-500/30
      hover:-translate-y-0.5
      focus:ring-emerald-500
    `,
    warning: `
      bg-gradient-to-r from-amber-500 to-orange-500
      hover:from-amber-400 hover:to-orange-400
      text-white shadow-lg shadow-amber-500/25
      focus:ring-amber-500
    `,
    info: `
      bg-gradient-to-r from-blue-500 to-blue-600
      hover:from-blue-400 hover:to-blue-500
      text-white shadow-lg shadow-blue-500/25
      focus:ring-blue-500
    `,
    ghost: `
      bg-transparent
      hover:bg-slate-800/50
      text-slate-300 hover:text-white
      focus:ring-slate-500
    `,
  };

  const sizes = {
    sm: "px-3 py-1.5 text-sm gap-1.5",
    md: "px-5 py-2.5 text-sm gap-2",
    lg: "px-6 py-3 text-base gap-2",
    xl: "px-8 py-4 text-lg gap-3",
  };

  const glowEffect = glow ? "shadow-glow-primary hover:shadow-glow-lg" : "";

  return (
    <button
      className={cn(
        baseStyles,
        variants[variant],
        sizes[size],
        fullWidth && "w-full",
        glowEffect,
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <>
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>Loading...</span>
        </>
      ) : (
        <>
          {icon && <span className="flex-shrink-0 w-4 h-4">{icon}</span>}
          {children}
          {iconRight && <span className="flex-shrink-0 w-4 h-4">{iconRight}</span>}
        </>
      )}
    </button>
  );
};
